import React, { ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import BSNavDropdown from 'react-bootstrap/NavDropdown'
import styled from 'styled-components'
import { Dropdown, Form } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import {
    doNotDisturbSelector,
    editProfile,
    setDoNotDisturb,
    toggleProfile
} from '../../../features/profile/editProfileSlice'
import StyledIcon from '../../Icon'
import { itemStyle } from '../styles'
import { _ } from '../../../utils/localization'
import NavLink from '../NavLink'
import routes from '../../../routes'
import colors from '../../../constants/colors'
import { currentUserSelector } from '../../../features/auth/authSlice'
import useSlug from '../../../hooks/useSlug'
import { eventSelector } from '../../../features/events/eventSlice'
import useDebounce from '../../../hooks/useDebounce'

const S = {
    DropdownItem: styled(BSNavDropdown.Item)`
        padding: 8px 10px!important;
        &:active, &:focus {
            background-color: inherit!important;
        }
    `,
    ProfileDropdownToggle: styled.div`
        display: flex;
        min-width: 180px;
        width: 100%;
        align-items: flex-end;
        padding: 0 10px;
        cursor: pointer;
        color: rgba(0, 0, 0, 0.5);
        font-size: 14px;
        &:hover {
            color: black;
            & svg:not(.video-icon) path {
                fill: black!important;
            }
        }
    `,
    ProfileDropdownMenu: styled.div`
        min-width: 180px;
        border: none;
        border-radius: 0;
    `
}

interface DropdownProps {
    children: ReactNode
    onClick?: (e) => void
    style?: React.CSSProperties
    className?: string
}
type Ref = HTMLDivElement

const ProfileDropdownToggle = React.forwardRef<Ref, DropdownProps>(
    ({ children, onClick }: DropdownProps, ref) => (
        <S.ProfileDropdownToggle
            ref={ref}
            onClick={(e) => {
                onClick(e)
            }}
        >
            {children}
            <StyledIcon icon="chevronDown" size={18} style={{ marginLeft: 10 }} />
        </S.ProfileDropdownToggle>
    )
)

const ProfileDropdownMenu = React.forwardRef<Ref, DropdownProps>(
    ({ children, style, className }: DropdownProps, ref) => (
        <S.ProfileDropdownMenu
            ref={ref}
            className={className}
            style={style}
        >
            {children}
        </S.ProfileDropdownMenu>
    )
)

const ProfileToggle = () => {
    const dispatch = useDispatch()
    const onClick = () => {
        dispatch(toggleProfile())
    }
    return (
        <S.DropdownItem onClick={onClick} style={itemStyle} className="nav-link">
            <StyledIcon icon="pencil" size={18} style={{ marginRight: 5 }} />
            <span>{ _('nav.edit_profile') }</span>
        </S.DropdownItem>
    )
}

const DoNotDisturb = () => {
    const dispatch = useDispatch()
    const currentUser = useSelector(currentUserSelector())
    const doNotDisturb = useSelector(doNotDisturbSelector)

    // Don't send every update immediately to the server because:
    // - We can get race conditions
    // - Prevent load: this sends websocket messages to users in the event
    // - Exact same websocket send within 3 seconds messages are filtered
    const debounceToggle = useDebounce((newValue) => {
        dispatch(editProfile({ id: currentUser.id, do_not_disturb: newValue }))
    }, 2000)

    const update = (v: boolean) => {
        dispatch(setDoNotDisturb(v))
        debounceToggle(v)
    }

    if (!currentUser?.id) {
        return null
    }

    return (
        <div style={itemStyle}>
            <S.DropdownItem
                style={{ ...itemStyle, minWidth: 110, pointerEvents: 'none' }}
                className="nav-link"
            >
                <StyledIcon icon="videoOff" size={18} style={{ marginRight: 5 }} />
                <span>{ _('nav.do_not_disturb') }</span>
            </S.DropdownItem>
            <Form.Group controlId="disturb-toggle" style={{ margin: 0 }}>
                <Form.Check
                    type="switch"
                    label=""
                    checked={doNotDisturb}
                    onChange={() => update(!doNotDisturb)}
                />
            </Form.Group>
        </div>
    )
}

const DndIcon = () => {
    const currentUser = useSelector(currentUserSelector())
    return currentUser.do_not_disturb ? (
        <StyledIcon icon="videoOff" size={18} style={{ marginLeft: 20 }} className="video-icon" color={colors.danger} />
    ) : <StyledIcon icon="video" size={18} style={{ marginLeft: 20 }} className="video-icon" color={colors.success} />
}

const ProfileDropdown = () => {
    const eventSlug = useSlug('eventSlug')
    const history = useHistory()
    const event = useSelector(eventSelector())

    return (
        <Dropdown style={{ ...itemStyle, width: 180 }}>
            <Dropdown.Toggle as={ProfileDropdownToggle}>
                <StyledIcon icon="personCircle" size={18} style={{ marginRight: 5, paddingBottom: 1 }} />
                <span>{_('nav.profile')}</span>
                <div style={{ flex: 1 }} />
                <DndIcon />
            </Dropdown.Toggle>

            <Dropdown.Menu as={ProfileDropdownMenu}>
                <ProfileToggle />
                <S.DropdownItem onClick={() => history.push(`/${eventSlug}/goodiebag`)} style={itemStyle} className="nav-link">
                    <StyledIcon icon="goodiebag" size={18} style={{ marginRight: 5 }} />
                    <span>{event.goodiebag_name}</span>
                </S.DropdownItem>
                <DoNotDisturb />
                <NavLink
                    text={_('nav.sign_out')}
                    icon="exit"
                    path={routes.logout.getPath()}
                />
            </Dropdown.Menu>
        </Dropdown>
    )
}

export default ProfileDropdown
